import { useCallback, useEffect, useMemo, useState } from 'react';
import { canGuestAccessSite, isValidEmailFormat, RSVP_STATUSES } from './guestAllowlist';
import { useAdminTheme } from './adminTheme';

const DRAFT_KEY = 'weddingMailoutDraftV1';
const MAX_MAILTO_LEN = 1900;

const RSVP_ORDER = [
  RSVP_STATUSES.NOT_SENT,
  RSVP_STATUSES.PENDING,
  RSVP_STATUSES.CONFIRMED,
  RSVP_STATUSES.DECLINED,
];

const RSVP_LABEL = {
  [RSVP_STATUSES.NOT_SENT]: 'Not sent',
  [RSVP_STATUSES.PENDING]: 'Pending',
  [RSVP_STATUSES.CONFIRMED]: 'Confirmed',
  [RSVP_STATUSES.DECLINED]: 'Declined',
};

function mergeTemplate(template, guest) {
  return String(template || '')
    .replace(/\{\{\s*firstName\s*\}\}/gi, guest.firstName || 'there')
    .replace(/\{\{\s*email\s*\}\}/gi, guest.email || '')
    .replace(/\{\{\s*group\s*\}\}/gi, (guest.group || '').trim() || '—');
}

/** One shared BCC message cannot be personalized per guest; use neutral wording for mailto. */
const MAILTO_NEUTRAL_GUEST = { firstName: 'there', email: '', group: '' };

function loadDraft() {
  if (typeof localStorage === 'undefined') return { subject: '', body: '' };
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return { subject: '', body: '' };
    const p = JSON.parse(raw);
    return {
      subject: typeof p.subject === 'string' ? p.subject : '',
      body: typeof p.body === 'string' ? p.body : '',
    };
  } catch {
    return { subject: '', body: '' };
  }
}

function saveDraft(subject, body) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ subject, body }));
  } catch {
    /* ignore */
  }
}

export function AdminMailoutsTab({ guests, tagsOrdered = [] }) {
  const { theme } = useAdminTheme();
  const [{ subject, body }, setDraft] = useState(loadDraft);
  const [rsvpOn, setRsvpOn] = useState(false);
  const [rsvpPick, setRsvpPick] = useState(() => new Set(RSVP_ORDER));
  const [tagOn, setTagOn] = useState(false);
  const [tagPick, setTagPick] = useState(() => new Set());
  const [previewGuestId, setPreviewGuestId] = useState('');
  const [sendNotice, setSendNotice] = useState('');

  const tagKeys = useMemo(() => tagsOrdered.map((t) => t.key), [tagsOrdered]);

  useEffect(() => {
    if (!tagOn || !tagKeys.length) return;
    setTagPick((prev) => {
      if (!prev.size) return new Set(tagKeys);
      const next = new Set(prev);
      let changed = false;
      for (const k of tagKeys) {
        if (!next.has(k)) {
          next.add(k);
          changed = true;
        }
      }
      for (const k of [...next]) {
        if (!tagKeys.includes(k)) {
          next.delete(k);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [tagOn, tagKeys]);

  const recipients = useMemo(() => {
    return guests.filter((g) => {
      if (!canGuestAccessSite(g)) return false;
      if (!isValidEmailFormat(g.email)) return false;
      if (rsvpOn) {
        if (!rsvpPick.has(g.rsvpStatus)) return false;
      }
      if (tagOn) {
        const lower = (g.tags || []).map((x) => String(x).toLowerCase());
        const any = [...tagPick].some((k) => lower.includes(k));
        if (!any) return false;
      }
      return true;
    });
  }, [guests, rsvpOn, rsvpPick, tagOn, tagPick]);

  const recipientKey = useMemo(() => recipients.map((g) => g.id).join('\u001f'), [recipients]);

  const previewGuest = useMemo(() => {
    if (!recipients.length) return null;
    if (previewGuestId) {
      const found = recipients.find((g) => g.id === previewGuestId);
      if (found) return found;
    }
    return recipients[0];
  }, [recipients, previewGuestId]);

  useEffect(() => {
    if (previewGuestId && recipients.some((g) => g.id === previewGuestId)) return;
    setPreviewGuestId(recipients[0]?.id ?? '');
  }, [recipientKey, previewGuestId, recipients]);

  const persistDraft = useCallback((next) => {
    setDraft(next);
    saveDraft(next.subject, next.body);
  }, []);

  const toggleRsvp = useCallback((status) => {
    setRsvpPick((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        if (next.size <= 1) return next;
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const toggleMailoutTag = useCallback((key) => {
    setTagPick((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size <= 1) return next;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const tagLabel = useCallback(
    (key) => tagsOrdered.find((t) => t.key === key)?.label ?? key,
    [tagsOrdered],
  );

  const selectAllRsvp = useCallback(() => {
    setRsvpPick(new Set(RSVP_ORDER));
  }, []);

  const copyEmails = useCallback(() => {
    const text = recipients.map((g) => g.email).join(', ');
    if (!text) {
      setSendNotice('No recipients with valid emails.');
      return;
    }
    void navigator.clipboard?.writeText(text).then(
      () => setSendNotice(`Copied ${recipients.length} address(es) (comma-separated) for BCC.`),
      () => setSendNotice('Clipboard failed — select and copy from the list below.'),
    );
  }, [recipients]);

  const openMailto = useCallback(() => {
    if (!recipients.length) {
      setSendNotice('No recipients with valid emails.');
      return;
    }
    const bcc = recipients.map((g) => g.email).join(',');
    const q = new URLSearchParams();
    q.set('bcc', bcc);
    if (subject.trim()) q.set('subject', mergeTemplate(subject, MAILTO_NEUTRAL_GUEST));
    const bodyMerged = mergeTemplate(body, MAILTO_NEUTRAL_GUEST);
    if (bodyMerged.trim()) q.set('body', bodyMerged);
    const href = `mailto:?${q.toString()}`;
    if (href.length > MAX_MAILTO_LEN) {
      setSendNotice(
        `Link too long for mail app (${href.length} chars). Use Copy emails and paste into BCC in Gmail or Outlook.`,
      );
      return;
    }
    window.location.href = href;
    setSendNotice('Opened your default mail app. Recipients are in BCC; check before sending.');
  }, [recipients, subject, body]);

  const btn = {
    font: 'inherit',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 6,
  };

  const chip = (active, onClick, label) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      style={{
        ...btn,
        padding: '5px 10px',
        fontSize: 12,
        border: `1px solid ${active ? theme.accent : theme.border}`,
        background: active ? theme.accentTintStrong : theme.bgInput,
        color: active ? theme.text : theme.muted,
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      className="admin-mailouts-tab"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: theme.bg,
      }}
    >
      <style>{`
        .admin-mailouts-tab__grid {
          overflow: visible;
          padding: 16px 20px 32px;
          display: grid;
          grid-template-columns: minmax(280px, 1fr) minmax(280px, 1fr);
          gap: 16px;
          align-content: start;
        }
        @media (max-width: 900px) {
          .admin-mailouts-tab__grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 640px) {
          .admin-mailouts-tab__grid {
            padding: 12px max(12px, env(safe-area-inset-left)) max(28px, env(safe-area-inset-bottom))
              max(12px, env(safe-area-inset-right));
          }
        }
      `}</style>
      <div className="admin-mailouts-tab__grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <section
            style={{
              padding: 12,
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              background: theme.bgPanel,
            }}
          >
            <div style={{ fontSize: 11, color: theme.muted, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 }}>
              RECIPIENTS
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: theme.muted, lineHeight: 1.45 }}>
              {recipients.length} guest{recipients.length === 1 ? '' : 's'} with a valid email match the filters below.
              Rows without a valid address are skipped.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: theme.text }}>
              <input
                type="checkbox"
                checked={rsvpOn}
                onChange={(e) => {
                  setRsvpOn(e.target.checked);
                  if (e.target.checked) selectAllRsvp();
                }}
              />
              Filter by RSVP status
            </label>
            {rsvpOn ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {RSVP_ORDER.map((s) => chip(rsvpPick.has(s), () => toggleRsvp(s), RSVP_LABEL[s]))}
              </div>
            ) : null}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: theme.text }}>
              <input
                type="checkbox"
                checked={tagOn}
                onChange={(e) => {
                  const on = e.target.checked;
                  setTagOn(on);
                  if (on) setTagPick(new Set(tagKeys));
                }}
              />
              Filter by tag
            </label>
            {tagOn && tagKeys.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tagKeys.map((k) => chip(tagPick.has(k), () => toggleMailoutTag(k), tagLabel(k)))}
              </div>
            ) : tagOn ? (
              <p style={{ margin: 0, fontSize: 12, color: theme.muted }}>No tags in registry yet.</p>
            ) : null}
          </section>

          <section
            style={{
              padding: 12,
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              background: theme.bgPanel,
            }}
          >
            <div style={{ fontSize: 11, color: theme.muted, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 }}>
              COMPOSE
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: theme.muted, lineHeight: 1.45 }}>
              Use <code style={{ color: theme.accent }}>{'{{firstName}}'}</code>,{' '}
              <code style={{ color: theme.accent }}>{'{{email}}'}</code>, <code style={{ color: theme.accent }}>{'{{group}}'}</code>{' '}
              in subject or body. Preview shows each guest; <strong style={{ color: theme.text }}>Open in mail app</strong> sends{' '}
              one BCC blast with <code style={{ fontSize: 11, color: theme.muted }}>{'{{firstName}}'}</code> → “there” (edit in your
              mail app for a true mail merge). Draft saves in this browser.
            </p>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: theme.muted }} htmlFor="mailout-subject">
              Subject
            </label>
            <input
              id="mailout-subject"
              type="text"
              value={subject}
              onChange={(e) => persistDraft({ subject: e.target.value, body })}
              placeholder="Save the date — Meg & Ben"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                marginBottom: 10,
                padding: '8px 10px',
                borderRadius: 6,
                border: `1px solid ${theme.border}`,
                background: theme.bgInput,
                color: theme.text,
                font: 'inherit',
                fontSize: 14,
              }}
            />
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: theme.muted }} htmlFor="mailout-body">
              Body
            </label>
            <textarea
              id="mailout-body"
              value={body}
              onChange={(e) => persistDraft({ subject, body: e.target.value })}
              placeholder={`Hi {{firstName}},\n\nWe’d love to see you…`}
              rows={12}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: 6,
                border: `1px solid ${theme.border}`,
                background: theme.bgInput,
                color: theme.text,
                font: 'inherit',
                fontSize: 14,
                lineHeight: 1.45,
                resize: 'vertical',
                minHeight: 160,
              }}
            />
          </section>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              onClick={openMailto}
              style={{
                ...btn,
                padding: '10px 16px',
                border: 'none',
                background: theme.accent,
                color: theme.onAccent,
                minHeight: 44,
              }}
            >
              Open in mail app (BCC)
            </button>
            <button
              type="button"
              onClick={copyEmails}
              style={{
                ...btn,
                padding: '10px 16px',
                border: `1px solid ${theme.border}`,
                background: theme.bgInput,
                color: theme.text,
              }}
            >
              Copy recipient emails
            </button>
          </div>
          {sendNotice ? (
            <p style={{ margin: 0, fontSize: 12, color: theme.muted }} role="status">
              {sendNotice}
            </p>
          ) : null}
          <p style={{ margin: 0, fontSize: 11, color: theme.muted, lineHeight: 1.4 }}>
            This page cannot send mail on its own. “Open in mail app” builds a <code style={{ fontSize: 10 }}>mailto:</code>{' '}
            link (length limits apply). For bulk sends, use Copy emails or an email service (Mailchimp, etc.).
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <section
            style={{
              padding: 12,
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              background: theme.bgPanel,
              flex: 1,
              minHeight: 200,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ fontSize: 11, color: theme.muted, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 }}>
              PREVIEW
            </div>
            {recipients.length ? (
              <>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: theme.muted }} htmlFor="mailout-preview-as">
                  Preview as
                </label>
                <select
                  id="mailout-preview-as"
                  value={previewGuestId}
                  onChange={(e) => setPreviewGuestId(e.target.value)}
                  style={{
                    marginBottom: 12,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${theme.border}`,
                    background: theme.bgInput,
                    color: theme.text,
                    font: 'inherit',
                    fontSize: 13,
                  }}
                >
                  {recipients.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.firstName || '—'} · {g.email}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    padding: 12,
                    borderRadius: 6,
                    border: `1px solid ${theme.border}`,
                    background: theme.bgMuted,
                    flex: 1,
                    overflow: 'auto',
                  }}
                >
                  <div style={{ fontSize: 11, color: theme.muted, marginBottom: 6 }}>Subject</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 14 }}>
                    {mergeTemplate(subject, previewGuest) || '(no subject)'}
                  </div>
                  <div style={{ fontSize: 11, color: theme.muted, marginBottom: 6 }}>Body</div>
                  <div
                    style={{
                      fontSize: 14,
                      color: theme.text,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {mergeTemplate(body, previewGuest) || '(empty body)'}
                  </div>
                </div>
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: theme.muted }}>No recipients — adjust filters or add guest emails.</p>
            )}
          </section>

          <section
            style={{
              padding: 12,
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              background: theme.bgPanel,
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            <div style={{ fontSize: 11, color: theme.muted, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 }}>
              RECIPIENT LIST
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: theme.text, lineHeight: 1.6 }}>
              {recipients.map((g) => (
                <li key={g.id}>
                  <span style={{ color: theme.muted }}>{RSVP_LABEL[g.rsvpStatus] || g.rsvpStatus}</span>
                  {' · '}
                  {g.firstName || '—'} &lt;{g.email}&gt;
                  <span style={{ color: theme.muted }}>
                    {' '}
                    · {(g.tags || []).length ? (g.tags || []).join(', ') : '—'}
                  </span>
                </li>
              ))}
            </ul>
            {!recipients.length ? <p style={{ margin: 0, fontSize: 12, color: theme.muted }}>—</p> : null}
          </section>
        </div>
      </div>
    </div>
  );
}
